import { Phone } from "lucide-react";
import {
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { InfinitePagination } from "../misc/InfinitePagination";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { Lead } from "../types";
import { ClaimLeadButton } from "./ClaimLeadButton";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { ResponseTime } from "./ResponseTime";

/** Unterwegs zählt nur: Wer wartet, wie lange schon, übernehmen, anrufen. */
const LeadCard = ({ lead }: { lead: Lead }) => {
  const translate = useTranslate();
  const name =
    [lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
    lead.phone ||
    `#${lead.id}`;

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/leads/${lead.id}/show`} className="no-underline">
            <div className="flex flex-col">
              <span className="font-medium">{name}</span>
              <span className="text-xs text-muted-foreground">
                {translate(`resources.leads.source.${lead.source}`)}
                {lead.campaign ? ` · ${lead.campaign}` : ""}
              </span>
            </div>
          </Link>
          <LeadStatusBadge status={lead.status} />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {formatDistanceToNow(new Date(lead.created_at), {
              addSuffix: true,
              locale: de,
            })}
          </span>
          <span>
            {translate("resources.leads.fields.first_response_seconds")}:{" "}
            <ResponseTime seconds={lead.first_response_seconds} />
          </span>
        </div>

        <div className="flex items-center gap-2">
          {lead.phone ? (
            <Button variant="outline" size="sm" asChild className="flex-1">
              <a href={`tel:${lead.phone.replace(/\s/g, "")}`}>
                <Phone className="h-4 w-4" />
                {translate("resources.leads.action.call")}
              </a>
            </Button>
          ) : null}
          {lead.sales_id == null ? (
            <div className="flex-1">
              <ClaimLeadButton lead={lead} />
            </div>
          ) : (
            <span className="flex-1 text-xs text-muted-foreground text-right">
              {lead.sales_name}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const LeadListLayoutMobile = () => {
  const { data, isPending, error } = useListContext<Lead>();
  const translate = useTranslate();

  return (
    <div>
      <MobileHeader>
        <h1 className="text-lg font-semibold">
          {translate("resources.leads.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <div className="flex flex-col gap-3">
          {!isPending && !data?.length ? (
            <p className="text-sm text-muted-foreground">
              {translate("resources.leads.empty.description")}
            </p>
          ) : null}
          {data?.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
          {!error && (
            <div className="flex justify-center">
              <InfinitePagination />
            </div>
          )}
        </div>
      </MobileContent>
    </div>
  );
};

export const LeadListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "created_at", order: "DESC" }}
    >
      <LeadListLayoutMobile />
    </InfiniteListBase>
  );
};
